export type InboxItemKind =
  | "REQUISITION_APPROVAL"
  | "REQUISITION_EXCEPTION_REASON"
  | "FORECAST_SUBMISSION";

export type InboxCategory =
  | "EVERYTHING"
  | "APPROVALS"
  | "EXCEPTION_REASONS"
  | "FORECASTS";

export type InboxCategoryCount = {
  category: InboxCategory;
  count: number;
};

export type ParsedInboxKey = {
  kind: InboxItemKind;
  subjectId: string;
  qualifier: string;
};

const SEP = "~";

/** encodeURIComponent leaves `~` unreserved; it is our separator. */
const encodePart = (value: string): string =>
  encodeURIComponent(value).replaceAll("~", "%7E");

export const isInboxItemKind = (value: string): value is InboxItemKind =>
  value === "REQUISITION_APPROVAL" ||
  value === "REQUISITION_EXCEPTION_REASON" ||
  value === "FORECAST_SUBMISSION";

export const inboxItemKey = (
  kind: InboxItemKind,
  subjectId: string,
  qualifier: string
): string =>
  `${kind}${SEP}${encodePart(subjectId)}${SEP}${encodePart(qualifier)}`;

export const parseInboxItemKey = (key: string): ParsedInboxKey | null => {
  const parts = key.split(SEP);
  const kind = parts[0] ?? "";
  if (parts.length !== 3 || !isInboxItemKind(kind)) {
    return null;
  }
  try {
    const subjectId = decodeURIComponent(parts[1] ?? "");
    const qualifier = decodeURIComponent(parts[2] ?? "");
    if (subjectId === "" || qualifier === "") {
      return null;
    }
    return { kind, subjectId, qualifier };
  } catch {
    return null;
  }
};

export const categoryOf = (
  kind: InboxItemKind
): Exclude<InboxCategory, "EVERYTHING"> => {
  switch (kind) {
    case "REQUISITION_APPROVAL":
      return "APPROVALS";
    case "REQUISITION_EXCEPTION_REASON":
      return "EXCEPTION_REASONS";
    case "FORECAST_SUBMISSION":
      return "FORECASTS";
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

export const INBOX_FILTERS = [
  { value: "EVERYTHING", label: "Everything" },
  { value: "APPROVALS", label: "Approvals" },
  { value: "EXCEPTION_REASONS", label: "Exception reasons" },
  { value: "FORECASTS", label: "Forecasts" },
] as const satisfies readonly { value: InboxCategory; label: string }[];

const KIND_PARAM = "kind";
const ROUTE_PARAM = "route";
const TEAM_PARAM = "team";

export const isInboxCategory = (value: string): value is InboxCategory =>
  INBOX_FILTERS.some((item) => item.value === value);

export type InboxSearchFilters = {
  category: InboxCategory;
  routeId: string | null;
  /** Team nodes in URL order. Repeated `team` params. The server expands descendants. */
  teamIds: string[];
};

const teamIdsFromParams = (params: URLSearchParams): string[] => {
  const ids: string[] = [];
  for (const id of params.getAll(TEAM_PARAM)) {
    if (id !== "" && !ids.includes(id)) {
      ids.push(id);
    }
  }
  return ids;
};

export const soleTeamId = (teamIds: readonly string[]): string | null =>
  teamIds.length === 1 ? (teamIds[0] ?? null) : null;

export const filtersFromSearch = (search: string): InboxSearchFilters => {
  const params = new URLSearchParams(search);
  const kind = params.get(KIND_PARAM);
  const route = params.get(ROUTE_PARAM);
  return {
    category: kind && isInboxCategory(kind) ? kind : "EVERYTHING",
    routeId: route ? route : null,
    teamIds: teamIdsFromParams(params),
  };
};

export const searchFromFilters = ({
  category,
  routeId,
  teamIds,
}: InboxSearchFilters): string => {
  const params = new URLSearchParams();
  if (category !== "EVERYTHING") {
    params.set(KIND_PARAM, category);
  }
  if (routeId) {
    params.set(ROUTE_PARAM, routeId);
  }
  for (const id of teamIds) {
    if (id) {
      params.append(TEAM_PARAM, id);
    }
  }
  const value = params.toString().replaceAll("%7E", "~");
  return value ? `?${value}` : "";
};

export const categoryFromSearch = (search: string): InboxCategory =>
  filtersFromSearch(search).category;

export const withCategorySearch = (category: InboxCategory): string =>
  searchFromFilters({ category, routeId: null, teamIds: [] });

export const mergeInboxSearch = (
  current: InboxSearchFilters,
  next: Partial<InboxSearchFilters>
): string =>
  searchFromFilters({
    category: next.category ?? current.category,
    routeId: next.routeId === undefined ? current.routeId : next.routeId,
    teamIds: next.teamIds === undefined ? current.teamIds : next.teamIds,
  });

export const isUnknownInboxRoute = (
  routes: readonly { id: string }[],
  routeId: string | null,
  routesLoaded: boolean
): boolean =>
  routesLoaded &&
  routeId != null &&
  !routes.some((route) => route.id === routeId);

export const inboxFilterLabels = ({
  category,
  routeId,
  routes,
  teamLabel = null,
}: {
  category: InboxCategory;
  routeId: string | null;
  routes: readonly { id: string; label: string }[];
  teamLabel?: string | null;
}): {
  categoryLabel: string | null;
  routeLabel: string | null;
  teamLabel: string | null;
} => ({
  categoryLabel:
    category === "EVERYTHING"
      ? null
      : (INBOX_FILTERS.find((item) => item.value === category)?.label ?? null),
  routeLabel:
    routeId == null
      ? null
      : (routes.find((route) => route.id === routeId)?.label ?? null),
  teamLabel,
});

export const inboxFilterIsEmpty = ({
  category,
  routeId,
  teamIds = [],
  routesLoaded,
  total,
  waiting,
  busy,
}: {
  category: InboxCategory;
  routeId: string | null;
  teamIds?: readonly string[];
  routesLoaded: boolean;
  total: number;
  waiting: number;
  busy: boolean;
}): boolean =>
  (category !== "EVERYTHING" || routeId != null || teamIds.length > 0) &&
  total === 0 &&
  waiting > 0 &&
  !busy &&
  (routeId == null || routesLoaded);

export const inboxFilterEmptyTitle = ({
  categoryLabel,
  routeLabel,
  teamLabel,
}: {
  categoryLabel: string | null;
  routeLabel: string | null;
  teamLabel?: string | null;
}): string => {
  const under = teamLabel ? ` under ${teamLabel}` : "";
  if (categoryLabel && routeLabel) {
    return `Nothing in ${categoryLabel} via ${routeLabel}${under}`;
  }
  if (categoryLabel) {
    return `Nothing in ${categoryLabel}${under}`;
  }
  if (routeLabel) {
    return `Nothing via ${routeLabel}${under}`;
  }
  if (teamLabel) {
    return `Nothing under ${teamLabel}`;
  }
  return "Nothing in this filter";
};

export const inboxFilterCaption = ({
  categoryLabel,
  routeLabel,
  teamLabel,
  total,
  waiting,
}: {
  categoryLabel: string | null;
  routeLabel: string | null;
  teamLabel?: string | null;
  total: number;
  waiting: number;
}): string | null => {
  const bits = [categoryLabel, routeLabel, teamLabel].filter(
    (bit): bit is string => Boolean(bit)
  );
  if (bits.length === 0) {
    return null;
  }
  return `${bits.join(" · ")} · ${total} of ${waiting}`;
};

export const inboxBadgeTotal = (
  counts: readonly { category: string; count: number }[]
): number => {
  const everything = counts.find((row) => row.category === "EVERYTHING");
  if (everything) {
    return everything.count;
  }
  return counts.reduce((sum, row) => sum + row.count, 0);
};

export const countForCategory = (
  counts: readonly { category: string; count: number }[],
  category: string
): number => {
  if (category === "EVERYTHING") {
    return inboxBadgeTotal(counts);
  }
  return counts.find((row) => row.category === category)?.count ?? 0;
};

/** All-routes option: category ∧ team, route filter off. Caller supplies the count. */
export const inboxAllRoutesTotal = ({
  allRoutesCount,
  category,
  total,
  counts,
}: {
  allRoutesCount?: number | null | undefined;
  category: string;
  total: number;
  counts: readonly { category: string; count: number }[];
}): number =>
  allRoutesCount ??
  (category === "EVERYTHING" ? total : countForCategory(counts, category));

export const inboxFilterClearLabel = ({
  category,
  routeId,
  teamIds,
}: InboxSearchFilters): string =>
  category === "EVERYTHING" && routeId == null && teamIds.length > 0
    ? "Clear team"
    : "Clear filters";

export const itemInCategory = (
  kind: InboxItemKind,
  category: InboxCategory
): boolean => category === "EVERYTHING" || categoryOf(kind) === category;

export const otherCategoryCount = (
  counts: readonly InboxCategoryCount[],
  category: InboxCategory
): number => {
  if (category === "EVERYTHING") {
    return 0;
  }
  return counts
    .filter((row) => row.category !== category && row.category !== "EVERYTHING")
    .reduce((sum, row) => sum + row.count, 0);
};
